/**
 * Unit experience progression — pure functions, no game-state dependencies.
 *
 * **Progression curve**: cumulative kills required to reach level N follow the
 * classical Fibonacci sequence starting F₁ = 1, F₂ = 2:
 *
 *   level 1 ←  1 kill
 *   level 2 ←  1 + 2 = 3 kills
 *   level 3 ←  3 + 3 = 6 kills
 *   level 4 ←  6 + 5 = 11
 *   level 5 ←  11 + 8 = 19
 *   …
 *
 * **Bonus per level**: every level multiplies effective attack and defense by
 * +10% (multiplicative, not flat — `1 + 0.10 * level`).
 *
 * **Visual tiers**: bronze stars for levels 1–3, silver for 4–6, gold for 7+.
 * The number of rendered stars cycles 1→3 inside each tier, then resets when
 * the tier advances. Level ≥ 10 caps at "3 gold stars" visually, but gameplay
 * bonuses keep stacking.
 */

const FIB: number[] = (() => {
  // F₁ = 1, F₂ = 2; up to F₂₀ covers cumulative thresholds well into the
  // "you're never going to see this" range (T₂₀ ≈ 17,710 kills).
  const xs = [1, 2];
  for (let i = 2; i < 20; i++) xs.push(xs[i - 1] + xs[i - 2]);
  return xs;
})();

/** Cumulative kills required to *reach* the given level. Level 0 returns 0. */
export function fibThreshold(level: number): number {
  if (level <= 0) return 0;
  let sum = 0;
  for (let i = 0; i < level && i < FIB.length; i++) sum += FIB[i];
  return sum;
}

/** Highest level the given XP qualifies for. xp = 0 → level 0. */
export function levelForXp(xp: number): number {
  if (xp <= 0) return 0;
  let level = 0;
  let cumulative = 0;
  for (let i = 0; i < FIB.length; i++) {
    cumulative += FIB[i];
    if (xp >= cumulative) level = i + 1;
    else break;
  }
  return level;
}

/** Multiplier applied to attack and defense at the given level. */
export function xpMultiplier(level: number): number {
  return 1 + 0.10 * Math.max(0, level);
}

export type XpTier = 'bronze' | 'silver' | 'gold';

/** Tier bucket for a level. Levels ≥ 7 stay in gold forever. */
export function tierForLevel(level: number): XpTier {
  if (level <= 3) return 'bronze';
  if (level <= 6) return 'silver';
  return 'gold';
}

/** How many stars to render at the given level. Caps at 3 within each tier;
 *  beyond level 9 always 3 gold stars. */
export function starsForLevel(level: number): number {
  if (level <= 0) return 0;
  if (level >= 7) return Math.min(3, level - 6);
  if (level >= 4) return level - 3;
  return level; // 1, 2, 3 for bronze
}
